import React from 'react';
import { View, Text, Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteEventInstance, cancelEventInstance } from '@/data/events';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import Unicon from '../General/Unicon';
import DropdownMenu from '../General/DropdownMenu';
import { formatTimeWithTimezone } from '@/utils/timezone';
import Toast from 'react-native-toast-message';
import ServiceFlowExporter from '@/utils/ServiceFlowExporter';
import { getServiceFlowForInstance } from '@/data/serviceFlows';
import { getAccessibleTextColor } from '@/utils/colorAccessibility';
import { isServiceFlowV2Available } from '@/config/serviceFlows';

const EventInstanceCard = ({ instance, variant = 'default' }) => {
    const router = useRouter();
    const queryClient = useQueryClient();
    const { theme } = useUnistyles();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: organization } = Query.organization.byId({ id: loggedInUser?.organizationID, options: { enabled: !!loggedInUser?.organizationID } });

    const deleteEventMutation = useMutation({
        mutationFn: deleteEventInstance,
        onSuccess: () => refetchEvents(),
    });

    const cancelEventMutation = useMutation({
        mutationFn: cancelEventInstance,
        onSuccess: () => refetchEvents(),
    });

    const refetchEvents = () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.events.all() });
        queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.all() });
    };

    const startTime = new Date(instance.startTime || instance.Event?.defaultStartTime);
    const endTime = instance.endTime || instance.Event?.defaultEndTime ? new Date(instance.endTime || instance.Event?.defaultEndTime) : null;

    const locationDisplay = instance.Locations?.length > 0
        ? (instance.Locations?.length > 1
            ? `${instance.Locations?.length} Locations`
            : instance.Locations[0]?.name?.trim() + (instance.Locations[0]?.detailedArea ? " - " + instance.Locations[0]?.detailedArea.trim() : ""))
        : "";

    const options = [];
    loggedInUser?.can?.("Edit Events") && options.push(...[
        { name: "Edit This Occurrence", subtitle: "Edit this specific Event", onSelect: () => router.push({ pathname: `/events/instances/create`, params: { id: instance.id } }) },
        { name: "View Event Series", subtitle: "View this entire series", onSelect: () => router.push({ pathname: `/events/${instance?.Event?.id}` }) },
        { name: (instance.canceled ? "Reschedule Event" : "Cancel Event"), onSelect: () => cancelEventMutation.mutate({ eventInstanceID: instance.id, canceled: !instance.canceled }) },
    ]);
    loggedInUser?.can?.("Check In") && options.push({ name: "Check In", onSelect: () => router.push({ pathname: `/events/instances/${instance.id}/checkin` }) });

    const hasServiceFlowItems = (instance?.ServiceFlowInstances?.length > 0) ||
        instance?.TaskInstances?.some(t => t?.Task?.dueType === "EVENT_SERVICE_FLOW");
    hasServiceFlowItems && organization && !isServiceFlowV2Available(organization) && options.push({
        name: "Export Service Flow",
        subtitle: "Download as PDF",
        onSelect: async () => {
            try {
                const serviceFlowItems = await getServiceFlowForInstance(instance.id);
                await ServiceFlowExporter.exportToPDF({ ...instance, ServiceFlowInstances: serviceFlowItems });
                Toast.show({ type: 'success', text2: 'Service Flow exported successfully' });
            } catch {
                Toast.show({ type: 'error', text2: 'Failed to export Service Flow' });
            }
        },
    });

    loggedInUser?.can?.("Delete Events") && options.push({ name: "Delete this Occurrence", destructive: true, onSelect: () => deleteEventMutation.mutate({ eventInstanceID: instance.id, isDeleted: !instance.isDeleted }) });

    const calendarColor = instance.Event?.Calendar?.color;
    const dateBadgeColor = calendarColor || theme.colors.events;
    const dateTextColor = /^#[\da-f]{3,6}$/i.test(dateBadgeColor)
        ? getAccessibleTextColor(dateBadgeColor, theme.colors.onPrimary, theme.colors.text)
        : theme.colors.onPrimary;
    const isDisabled = instance.isProjection || instance.canceled;

    return (
        <View style={styles.wrapper}>
            <Pressable
                testID={`EventInstanceCard-${instance.nameOverride || instance.Event?.name}`}
                onPress={() => {
                    if (instance.isHoliday) return;
                    router.push({ pathname: "/events/instances/" + instance.id });
                }}
                style={styles.card(variant)}
            >
                <View style={styles.dateBadge(dateBadgeColor, isDisabled)}>
                    <Text style={styles.dateBadgeMonth(dateTextColor)}>
                        {startTime?.toLocaleString('default', { month: 'short', timeZone: organization?.timezone }).toUpperCase()}
                    </Text>
                    <Text style={styles.dateBadgeDay(dateTextColor)}>
                        {startTime?.toLocaleString('default', { day: 'numeric', timeZone: organization?.timezone })}
                    </Text>
                </View>

                <View style={styles.content(isDisabled, options.length > 0)}>
                    {/* Top row: name + status badges */}
                    <View style={styles.topRow}>
                        <View style={styles.titleWrap}>
                            <Text style={styles.eventName(instance.canceled)} numberOfLines={1}>
                                {instance.nameOverride || instance.Event?.name}
                            </Text>
                        </View>
                        {instance.canceled && <View style={styles.statusBadge("error")}><Text style={styles.statusBadgeText("error")}>Canceled</Text></View>}
                    </View>

                    {/* Info rows: time + location */}
                    <View style={styles.infoColumn}>
                        <View style={styles.infoItem}>
                            <Unicon name="time-outline" size={12} colorVariant="textTertiary" />
                            {instance.allDay ? (
                                <Text style={styles.infoText}>All Day</Text>
                            ) : (
                                <Text style={styles.infoText}>
                                    {formatTimeWithTimezone(startTime, organization?.timezone)}
                                    {endTime && ` – ${formatTimeWithTimezone(endTime, organization?.timezone)}`}
                                </Text>
                            )}
                        </View>

                        {!!locationDisplay && (
                            <View style={styles.infoItem}>
                                <Unicon name="location-outline" size={12} colorVariant="textTertiary" />
                                <Text style={styles.locationText} numberOfLines={1}>{locationDisplay}</Text>
                            </View>
                        )}
                    </View>
                </View>
            </Pressable>

            {/* Dropdown menu */}
            {!instance.isHoliday && options.length > 0 && (
                <View style={styles.menuWrap}>
                    <DropdownMenu
                        testID={`EventInstanceCardMenu-${instance.nameOverride || instance.Event?.name}`}
                        options={options}
                        triggerComponent={
                            <Pressable accessibilityRole="button" accessibilityLabel="Event options" style={styles.menuButton}>
                                <Unicon name="ellipsis-vertical" size={16} colorVariant="textSecondary" />
                            </Pressable>
                        }
                    />
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows, roundness, spacing, typography }) => ({
    wrapper: {
        marginBottom: spacing.xs,
    },
    card: (variant) => {
        const isInline = variant === 'alternate';
        return {
            marginVertical: isInline ? 0 : 2,
            marginHorizontal: isInline ? 0 : 5,
            flexDirection: "row",
            alignItems: "center",
            gap: spacing.sm + 2,
            paddingHorizontal: isInline ? spacing.sm : spacing.sm + 4,
            paddingVertical: isInline ? spacing.sm : spacing.sm + 2,
            backgroundColor: isInline ? 'transparent' : colors.surface,
            borderWidth: 0,
            borderRadius: isInline ? 10 : roundness.md,
            boxShadow: isInline ? shadows.none : shadows.card,
            overflow: "hidden",
            _web: {
                transition: "box-shadow 0.15s ease, background-color 0.15s ease",
                _hover: isInline
                    ? { backgroundColor: colors.hover }
                    : { boxShadow: shadows.cardHover },
            },
        };
    },
    dateBadge: (color, disabled) => ({
        width: 44,
        height: 44,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: color,
        opacity: disabled ? 0.5 : 1,
    }),
    dateBadgeMonth: (color) => ({
        fontSize: 9,
        fontFamily: fonts.bold,
        color,
        letterSpacing: 0.4,
    }),
    dateBadgeDay: (color) => ({
        fontSize: 18,
        fontFamily: fonts.bold,
        color,
        marginTop: -2,
    }),
    content: (disabled, hasMenu) => ({
        flex: 1,
        minWidth: 0,
        paddingRight: hasMenu ? spacing.lg : 0,
        opacity: disabled ? 0.5 : 1,
    }),
    topRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        marginBottom: 2,
    },
    titleWrap: {
        flex: 1,
        minWidth: 0,
    },
    eventName: (canceled) => ({
        // flex: 1,
        fontFamily: fonts.heading,
        fontSize: typography.h3,
        color: colors.text,
        alignItems: "center",
        textDecorationLine: canceled ? "line-through" : "none",
    }),
    statusBadge: (variant) => ({
        paddingHorizontal: spacing.xs + 2,
        paddingVertical: 2,
        borderRadius: 4,
        backgroundColor: colors[variant + "Container"] || colors.errorContainer,
    }),
    statusBadgeText: (variant) => ({
        fontSize: typography.micro,
        fontFamily: fonts.bold,
        color: colors[variant] || colors.error,
    }),
    infoColumn: {
        gap: 2,
    },
    infoItem: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs,
    },
    infoText: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    locationText: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textTertiary,
        maxWidth: 180,
    },
    menuWrap: {
        position: "absolute",
        top: 0,
        bottom: 0,
        right: spacing.xs,
        justifyContent: 'center',
    },
    menuButton: {
        padding: spacing.xs,
        borderRadius: 8,
        _web: {
            transition: "background-color 0.15s ease",
            _hover: { backgroundColor: colors.hoverStrong },
        },
    },
}));

export default React.memo(EventInstanceCard);